import Logo from './Logo';

/**
 * Two-column hero layout shared by Login / Register / AddDomain.
 * Left: dark brand panel with marketing copy.
 * Right: form card.
 */
export default function AuthLayout({ title, subtitle, children, footer }) {
  return (
    <div className="min-h-screen lg:grid lg:grid-cols-2">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-ink-gradient text-white p-10 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30 bg-brand-radial" />
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-brand-600 blur-3xl opacity-30" />
        <div className="absolute -bottom-32 -left-10 w-96 h-96 rounded-full bg-cyan-500 blur-3xl opacity-20" />

        <div className="relative">
          <Logo variant="white" linkTo={false} size="lg" />
        </div>

        <div className="relative space-y-6 max-w-md">
          <h2 className="text-4xl font-bold leading-tight text-balance">
            Be the answer AI engines cite — not the one they skip.
          </h2>
          <p className="text-white/70 leading-relaxed">
            Track every visit from ChatGPT, Claude, Perplexity, Gemini, and Copilot. Rewrite
            your pages for AI citation in one click. Audit your whole site in under a minute.
          </p>
          <ul className="space-y-3 text-sm text-white/90">
            {[
              'Real-time AI crawler + referral tracking',
              'AI-powered content rewriting with JSON-LD schema',
              '7-point GEO audit with per-page scoring (0–100)',
              'Auto-generated /llms.txt for AI crawlers',
            ].map((line) => (
              <li key={line} className="flex items-start gap-2">
                <svg
                  className="mt-0.5 h-5 w-5 flex-none text-cyan-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0L3.3 9.7a1 1 0 011.4-1.4L8.5 12l6.8-6.8a1 1 0 011.4 0z"
                    clipRule="evenodd"
                  />
                </svg>
                {line}
              </li>
            ))}
          </ul>
        </div>

        <div className="relative text-xs text-white/50">
          © {new Date().getFullYear()} RankOnAI, All Rights Reserved. Made with{' '}
          <span aria-label="love">❤️</span> by{' '}
          <a
            href="https://pixalab.ai/"
            target="_blank"
            rel="noreferrer"
            className="text-white/70 hover:text-white transition-colors"
          >
            PixaLab
          </a>{' '}
          | Powered by{' '}
          <a
            href="https://pixelnx.com/"
            target="_blank"
            rel="noreferrer"
            className="text-white/70 hover:text-white transition-colors"
          >
            PixelNX
          </a>
          .
        </div>
      </div>

      {/* Form column */}
      <div className="flex flex-col justify-center px-6 sm:px-10 py-10 bg-dotted">
        <div className="lg:hidden mb-8">
          <Logo linkTo={false} size="lg" />
        </div>
        <div className="w-full max-w-md mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-balance">{title}</h1>
            {subtitle && <p className="text-ink-400 mt-2">{subtitle}</p>}
          </div>
          <div className="card shadow-card">{children}</div>
          {footer && <div className="mt-6 text-sm text-center text-ink-400">{footer}</div>}
        </div>
      </div>
    </div>
  );
}
